import "../../styles/quote-details-items.css";

import { Button } from "@tql/ui-components-react/tql/buttons";
import { plusCircle } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useContext, useEffect, useRef } from "react";

import { useItemCalculation } from "~/features/quote/hooks/use-item-calculation.tsx";
import { CommodityHandlingUnit } from "~/shared/components/commodities/commodity-handling-unit.tsx";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { HandlingUnit } from "~/shared/types/quote-details-types.ts";

import { QuoteDetailsContext } from "../../../../context/quote-details-context.tsx";
import { defaultHandlingUnit } from "../../constants/quote-detail-constants.ts";
import { CommodityTotals } from "../commodity-totals/commodity-totals.tsx";

type QuoteCommoditiesWrapperProps = {
	itemValidationErrors?: Record<number, Set<string>>;
	onClearItemFieldError?: (itemId: number, fieldKey: string) => void;
	showHazmatDetails?: boolean;
	requireDescription?: boolean;
	hideSaveCommodity?: boolean;
	requireClass?: boolean;
	alwaysShowPieceCount?: boolean;
	requirePieceCountForHazmatOnly?: boolean;
	/** Opt-in label for the mobile inline "Add Another Unit" button (see
	 *  CommodityHandlingUnit's addAnotherUnitLabel prop). Only pass this from
	 *  Tender call sites - leaving it unset preserves desktop Quote Details'
	 *  original "Handling Unit" copy at any viewport width. */
	mobileAddAnotherUnitLabel?: string;
};

const TOTALS_DEBOUNCE_MS = 500;

export const QuoteCommoditiesWrapper = ({
	itemValidationErrors,
	onClearItemFieldError,
	showHazmatDetails,
	requireDescription,
	hideSaveCommodity = false,
	requireClass = true,
	alwaysShowPieceCount = false,
	requirePieceCountForHazmatOnly = false,
	mobileAddAnotherUnitLabel,
}: QuoteCommoditiesWrapperProps) => {
	const { quoteState, addItem, removeItem, updateItem } =
		useContext(QuoteDetailsContext);
	const { items } = quoteState;
	const isMobile = useIsMobile();

	const debounceRef = useRef<NodeJS.Timeout | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const {
		getCalculatedItemTotals,
		getItemsWithDensityAndEstClass,
		calculationSignature,
	} = useItemCalculation(items, quoteState.measuringType);

	// The effect below is keyed on the signature, so it needs the latest
	// callback without taking a dependency on it.
	const calculateRef = useRef(getCalculatedItemTotals);
	useEffect(() => {
		calculateRef.current = getCalculatedItemTotals;
	}, [getCalculatedItemTotals]);

	useEffect(() => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}
		// Supersede the in-flight request now rather than when the new debounce
		// expires — otherwise it can resolve during that window and publish totals
		// for inputs the user has already changed.
		abortRef.current?.abort();
		abortRef.current = null;

		debounceRef.current = setTimeout(() => {
			const controller = new AbortController();
			abortRef.current = controller;
			void calculateRef.current(controller.signal);
		}, TOTALS_DEBOUNCE_MS);
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [calculationSignature]);

	useEffect(() => () => abortRef.current?.abort(), []);

	const addNewItem = useCallback(
		(handlingUnit: HandlingUnit = defaultHandlingUnit) => addItem(handlingUnit),
		[addItem],
	);
	const clickRemoveItem = (id: number) => {
		removeItem(id);
	};

	return (
		<div id={"item-summary-data-wrapper"}>
			<div id={"add-item"}>
				{getItemsWithDensityAndEstClass.map((item, idx) => (
					<CommodityHandlingUnit
						key={item.id}
						addAnotherUnitLabel={mobileAddAnotherUnitLabel}
						alwaysShowPieceCount={alwaysShowPieceCount}
						handlingUnit={item}
						hideSaveCommodity={hideSaveCommodity}
						idx={idx}
						isSingular={items.length === 1}
						onAddAnotherUnit={
							isMobile && idx === items.length - 1
								? () => addNewItem()
								: undefined
						}
						onClearFieldError={
							onClearItemFieldError
								? (fieldKey: string) => onClearItemFieldError(item.id, fieldKey)
								: undefined
						}
						onRemoveOverride={clickRemoveItem}
						requireClass={requireClass}
						requireDescription={requireDescription}
						requirePieceCountForHazmatOnly={requirePieceCountForHazmatOnly}
						showHazmatDetails={showHazmatDetails}
						updateItem={updateItem}
						validationErrors={itemValidationErrors?.[item.id]}
					/>
				))}
				{isMobile ? null : (
					<div className={"add-handling-unit-btn-wrapper"}>
						<div className={"commodity-action-button"}>
							<Button
								onClick={() => addNewItem()}
								svgIcon={plusCircle}
								tabIndex={-1}
							>
								Handling Unit
							</Button>
						</div>
					</div>
				)}
				<CommodityTotals />
			</div>
		</div>
	);
};
